import React, { useEffect, useState, useCallback } from 'react';
import { useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Row, Col, Form, Button, Card, Collapse, InputGroup } from 'react-bootstrap';
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons';

import { isFieldError, getErrorMessage } from 'utils/formHelper';
import { FormInvalidFeedback } from 'components/aimsis/form';

// Visibility Logic Component
const VisibilityLogic = ({ pageIndex, questionIndex, control, register, setValue, watch, t }) => {
  const watchedQuestions = watch(`pages.${pageIndex}.questions`);
  const currentLogic = watch(`pages.${pageIndex}.questions.${questionIndex}.logic`);

  // Get all previous questions that can be used as triggers
  const getAvailableTriggerQuestions = () => {
    const questions = watchedQuestions || [];
    return questions
      .slice(0, questionIndex) // Only previous questions
      .map((question, index) => ({
        index,
        questionNumber: index + 1,
        questionText: question?.questionText || `${t('form.question_number')} ${index + 1}`,
        type: question?.type || 'text'
      }));
  };

  const availableQuestions = getAvailableTriggerQuestions();

  // Condition types based on question type
  const getConditionTypes = (questionType) => {
    const baseConditions = [
      { value: 'empty', label: t('form.condition_empty') },
      { value: 'notempty', label: t('form.condition_not_empty') },
      { value: 'equal', label: t('form.condition_equal') },
      { value: 'notequal', label: t('form.condition_not_equal') },
      { value: 'anyof', label: t('form.condition_any_of') },
      { value: 'greater', label: t('form.condition_greater') },
      { value: 'less', label: t('form.condition_less') },
      { value: 'greaterorequal', label: t('form.condition_greater_or_equal') },
      { value: 'lessorequal', label: t('form.condition_less_or_equal') },
    ];

    switch (questionType) {
      case 'text':
      case 'number':
      case 'rating':
      case 'date':
      case 'datetime':
      case 'checkbox':
      case 'radiogroup':
      case 'dropdown':
      case 'file':
      default:
        return [
          ...baseConditions,
        ];
    }
  };

  const handleTriggerQuestionChange = (triggerQuestionIndex) => {
    if (triggerQuestionIndex === '') {
      const currentLogic = watch(`pages.${pageIndex}.questions.${questionIndex}.logic`) || {};
      setValue(`pages.${pageIndex}.questions.${questionIndex}.logic`, {
        ...currentLogic,
        trigger_question_index: null,
        trigger_question_type: null,
        condition_type: null,
        condition_value: ''
      });
    } else {
      const selectedQuestion = availableQuestions.find(q => q.index === parseInt(triggerQuestionIndex));
      if (selectedQuestion) {
        const currentLogic = watch(`pages.${pageIndex}.questions.${questionIndex}.logic`) || {};
        setValue(`pages.${pageIndex}.questions.${questionIndex}.logic`, {
          ...currentLogic,
          trigger_question_index: parseInt(triggerQuestionIndex),
          trigger_question_type: selectedQuestion.type,
          condition_type: 'equal', // Reset to default
          condition_value: ''
        });
      }
    }
  };

  const handleConditionTypeChange = (conditionType) => {
    const currentLogic = watch(`pages.${pageIndex}.questions.${questionIndex}.logic`) || {};
    setValue(`pages.${pageIndex}.questions.${questionIndex}.logic`, {
      ...currentLogic,
      condition_type: conditionType,
      condition_value: '' // Reset value when condition type changes
    });
  };

  const handleActionChange = (action) => {
    const currentLogic = watch(`pages.${pageIndex}.questions.${questionIndex}.logic`) || {};
    setValue(`pages.${pageIndex}.questions.${questionIndex}.logic`, {
      ...currentLogic,
      action: action
    });
  };

  const removeLogic = () => {
    setValue(`pages.${pageIndex}.questions.${questionIndex}.logic`, null);
  };

  // Don't show logic for first question
  if (questionIndex === 0 || availableQuestions.length === 0) {
    return null;
  }

  const selectedTriggerIndex = currentLogic?.trigger_question_index;
  const selectedQuestion = availableQuestions.find(q => q.index === selectedTriggerIndex);
  const conditionTypes = selectedQuestion ? getConditionTypes(selectedQuestion.type) : [];

  return (
    <div className='mt-3 p-3 border rounded bg-light'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h6 className='mb-0'>{t('form.visibility_logic')}</h6>
        <Button type='button' variant='outline-danger' size='sm' onClick={removeLogic}>
          {t('form.remove_logic')}
        </Button>
      </div>

      <Row className='mb-3'>
        <Col md={12}>
          <Form.Group>
            <Form.Label>{t('form.show_question_when')}</Form.Label>
            <Form.Control
              as='select'
              value={selectedTriggerIndex != null && availableQuestions.some(q => q.index === selectedTriggerIndex) ? selectedTriggerIndex : ''}
              onChange={(e) => handleTriggerQuestionChange(e.target.value)}
              className='mb-2'
            >
              <option value=''>{t('form.select_question')}</option>
              {availableQuestions.map((question) => (
                <option key={question.index} value={question.index}>
                  {t('form.question')} {question.questionNumber}: {question.questionText.substring(0, 50)}...
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
      </Row>

      {selectedQuestion && (
        <>
          <Row className='mb-3'>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('form.condition')}</Form.Label>
                <Form.Control
                  as='select'
                  value={currentLogic?.condition_type || 'equal'}
                  onChange={(e) => handleConditionTypeChange(e.target.value)}
                >
                  {conditionTypes.map((condition) => (
                    <option key={condition.value} value={condition.value}>
                      {condition.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label>{t('form.value')}</Form.Label>
                {selectedQuestion.type === 'checkbox' && currentLogic?.condition_type === 'anyof' ? (
                  <Form.Control
                    type='text'
                    placeholder={t('form.enter_option_value')}
                    {...register(`pages.${pageIndex}.questions.${questionIndex}.logic.condition_value`)}
                  />
                ) : selectedQuestion.type === 'number' || selectedQuestion.type === 'rating' ? (
                  <Form.Control
                    type='number'
                    placeholder={t('form.enter_number')}
                    {...register(`pages.${pageIndex}.questions.${questionIndex}.logic.condition_value`)}
                  />
                ) : selectedQuestion.type === 'date' ? (
                  <Form.Control
                    type='date'
                    {...register(`pages.${pageIndex}.questions.${questionIndex}.logic.condition_value`)}
                  />
                ) : selectedQuestion.type === 'datetime' ? (
                  <Form.Control
                    type='datetime-local'
                    {...register(`pages.${pageIndex}.questions.${questionIndex}.logic.condition_value`)}
                  />
                ) : selectedQuestion.type === 'radiogroup' || selectedQuestion.type === 'dropdown' ? (
                  <Form.Control
                    type='text'
                    placeholder={t('form.enter_option_value')}
                    {...register(`pages.${pageIndex}.questions.${questionIndex}.logic.condition_value`)}
                  />
                ) : selectedQuestion.type === 'file' ? (
                  <div className='mt-2'>
                    <small className='text-muted'>{t('form.file_upload_condition_hint')}</small>
                  </div>
                ) : (
                  <Form.Control
                    type='text'
                    placeholder={t('form.enter_value')}
                    {...register(`pages.${pageIndex}.questions.${questionIndex}.logic.condition_value`)}
                  />
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className='mb-3'>
            <Col md={12}>
              <Form.Group>
                <Form.Label>{t('form.action')}</Form.Label>
                <Form.Control
                  as='select'
                  value={currentLogic?.action || 'show'}
                  onChange={(e) => handleActionChange(e.target.value)}
                >
                  <option value='show'>{t('form.show_this_question')}</option>
                  <option value='hide'>{t('form.hide_this_question')}</option>
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
        </>
      )}

      <div className='mt-2 text-muted small'>
        <em>{t('form.logic_help_text', { action: currentLogic?.action || 'show' })}</em>
      </div>
    </div>
  );
};

// Options Builder Component
const OptionsBuilder = ({ pageIndex, questionIndex, control, register, setValue, questionType, t }) => {
  const { fields: options, append: appendOption, remove: removeOption } = useFieldArray({
    control,
    name: `pages.${pageIndex}.questions.${questionIndex}.options`,
  });

  const addOption = () => {
    appendOption({
      label: '',
      value: '',
    });
  };

  const removeOptionItem = (index) => {
    removeOption(index);
  };

  const getOptionsLabel = () => {
    switch (questionType) {
      case 'rating':
        return t('form.rating_options') || 'Rating Range';
      case 'file':
        return t('form.file_options') || 'File Upload Settings';
      case 'checkbox':
        return t('form.checkbox_options') || 'Checkbox Options';
      case 'radiogroup':
        return t('form.radio_options') || 'Radio Button Options';
      case 'dropdown':
        return t('form.dropdown_options') || 'Dropdown Options';
      default:
        return t('form.options') || 'Options';
    }
  };

  // Handle rating range changes
  const handleRatingRangeChange = (type, value) => {
    const min = type === 'min' ? parseInt(value) || 1 : 1;
    const max = type === 'max' ? parseInt(value) || 5 : 5;

    // Generate rating options
    const newOptions = [];
    for (let i = min; i <= max; i++) {
      const starText = i === 1 ? `1 ${t('form.star_singular')}` : `${i} ${t('form.star_plural')}`;
      newOptions.push({
        label: starText,
        value: starText,
      });
    }

    // Update the options
    setValue(`pages.${pageIndex}.questions.${questionIndex}.options`, newOptions);
  };

  return (
    <div className='mt-3 p-3 border rounded bg-light'>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h6 className='mb-0'>{getOptionsLabel()}</h6>
        {questionType !== 'rating' && questionType !== 'file' && (
          <Button type='button' variant='outline-primary' size='sm' onClick={addOption}>
            {t('form.add_option') || 'Add Option'}
          </Button>
        )}
      </div>

      {questionType === 'rating' ? (
        // Rating: Min/Max inputs
        <Row>
          <Col md={6}>
            <Form.Group className='mb-2'>
              <Form.Label>{t('form.min_rating')}</Form.Label>
              <Form.Control
                type='number'
                min='1'
                max='10'
                defaultValue='1'
                onChange={(e) => handleRatingRangeChange('min', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className='mb-2'>
              <Form.Label>{t('form.max_rating')}</Form.Label>
              <Form.Control
                type='number'
                min='1'
                max='10'
                defaultValue='5'
                onChange={(e) => handleRatingRangeChange('max', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      ) : questionType === 'file' ? (
        // File: Upload settings
        <div>
          <Form.Group className='mb-3'>
            <Form.Check
              type='checkbox'
              label={t('form.upload_multiple_files')}
              {...register(`pages.${pageIndex}.questions.${questionIndex}.allowMultiple`)}
            />
          </Form.Group>

          <Form.Group className='mb-2'>
            <Form.Label>{t('form.allowed_file_extensions')}</Form.Label>
            <Form.Control
              type='text'
              placeholder='.jpg, .gif, .pdf, .doc, .docx'
              {...register(`pages.${pageIndex}.questions.${questionIndex}.allowedExtensions`)}
            />
            <Form.Text className='text-muted'>
              {t('form.file_extensions_help')}
            </Form.Text>
          </Form.Group>
        </div>
      ) : (
        // Checkbox, Radiogroup, Dropdown: Individual options
        <>
          {options.map((option, optionIndex) => (
            <Row key={option.id} className='mb-2 align-items-center'>
              <Col md={10}>
                <Form.Control
                  type='text'
                  placeholder={`${t('form.option_number')} ${optionIndex + 1}`}
                  {...register(`pages.${pageIndex}.questions.${questionIndex}.options.${optionIndex}.label`, {
                    required: true
                  })}
                  onChange={(e) => {
                    const newLabel = e.target.value;
                    // Set the value to match the label
                    setValue(`pages.${pageIndex}.questions.${questionIndex}.options.${optionIndex}.value`, newLabel);
                  }}
                />
              </Col>
              <Col md={2}>
                <Button
                  type='button'
                  variant='outline-danger'
                  size='sm'
                  onClick={() => removeOptionItem(optionIndex)}
                  title={t('form.remove_option')}
                >
                  {t('form.remove') || 'Remove'}
                </Button>
              </Col>
            </Row>
          ))}

          {options.length === 0 && questionType !== 'rating' && questionType !== 'file' && (
            <div className='text-center text-muted py-3'>
              <p>{t('form.no_options_added') || 'No options added yet'}</p>
              <Button type='button' variant='outline-primary' size='sm' onClick={addOption}>
                {t('form.add_first_option') || 'Add First Option'}
              </Button>
            </div>
          )}

          {/* "Lainnya" (Other) option checkbox - for all question types that support options */}
          {(questionType === 'checkbox' || questionType === 'radiogroup' || questionType === 'dropdown' || questionType === 'rating' || questionType === 'file') && (
            <div className='mt-3'>
              <Form.Check
                type='checkbox'
                label={t('form.other_option')}
                {...register(`pages.${pageIndex}.questions.${questionIndex}.allowOther`)}
              />
            </div>
          )}
        </>
      )}

      <div className='mt-2 text-muted small'>
        <em>{t('form.options_note')}</em>
      </div>
    </div>
  );
};

const QuestionBuilder = ({ pageIndex, control, register, setValue, errors, errMessages, watch, openVariableModal }) => {
  const { t } = useTranslation();
  const [collapsedQuestions, setCollapsedQuestions] = useState({});

  const { fields: questions, append: appendQuestion, remove: removeQuestion } = useFieldArray({
    control,
    name: `pages.${pageIndex}.questions`,
  });

  // Initialize all questions as collapsed by default
  useEffect(() => {
    if (questions.length > 0) {
      const initialCollapsedState = {};
      questions.forEach((_, index) => {
        initialCollapsedState[index] = true;
      });
      setCollapsedQuestions(initialCollapsedState);
    }
  }, [questions.length]);

  // Watch all questions to handle type changes
  const watchedQuestions = watch(`pages.${pageIndex}.questions`);

  // Add default options when question type changes to one that requires options
  useEffect(() => {
    if (watchedQuestions && Array.isArray(watchedQuestions)) {
      watchedQuestions.forEach((question, questionIndex) => {
        if (question && requiresOptions(question.type)) {
          const currentOptions = watchedQuestions[questionIndex]?.options || [];
          if (!currentOptions || currentOptions.length === 0) {
            // Add a default empty option
            setValue(`pages.${pageIndex}.questions.${questionIndex}.options`, [{ label: '', value: '' }]);
          }
        }
      });
    }
  }, [watchedQuestions, setValue, pageIndex, watch]);

  const questionTypes = [
    { value: 'text', label: t('form.question_type_text') },
    { value: 'date', label: t('form.question_type_date') },
    { value: 'datetime', label: t('form.question_type_datetime') },
    { value: 'number', label: t('form.question_type_number') },
    { value: 'rating', label: t('form.question_type_rating') },
    { value: 'checkbox', label: t('form.question_type_checkbox') },
    { value: 'radiogroup', label: t('form.question_type_radiogroup') },
    { value: 'dropdown', label: t('form.question_type_dropdown') },
    { value: 'file', label: t('form.question_type_file') },
  ];

  const questionTypeOptions = [
    { value: 'Competency', label: t('form.question_category_competency') },
    { value: 'Organizational Fit', label: t('form.question_category_organizational_fit') },
  ];

  const addQuestion = () => {
    const newQuestionIndex = questions.length;
    const newQuestion = {
      type: 'text',
      questionType: 'Competency',
      questionText: '',
      required: false,
      options: [],
      logic: null,
      order: questions.length + 1,
    };

    // Add default option for question types that require options
    if (['checkbox', 'radiogroup', 'dropdown'].includes(newQuestion.type)) {
      newQuestion.options = [{ label: '', value: '' }];
    }

    appendQuestion(newQuestion);

    // Set the new question as collapsed by default
    setCollapsedQuestions(prev => ({
      ...prev,
      [newQuestionIndex]: true
    }));
  };

  const removeQuestionItem = (index) => {
    removeQuestion(index);
  };

  const isQuestionCollapsed = useCallback((questionIndex) => {
    return collapsedQuestions && collapsedQuestions[questionIndex] === true;
  }, [collapsedQuestions]);

  const toggleQuestionCollapse = useCallback((questionIndex) => {
    setCollapsedQuestions(prev => ({
      ...prev,
      [questionIndex]: !(prev && prev[questionIndex])
    }));
  }, []);

  // Check if question type requires options
  const requiresOptions = (questionType) => {
    return ['rating', 'checkbox', 'radiogroup', 'dropdown', 'file'].includes(questionType);
  };

  return (
    <div>
      <div className='d-flex justify-content-between align-items-center mb-3'>
        <h6>{t('form.questions')}</h6>
        <div className='d-flex gap-2'>
          <Button
            type='button'
            variant='outline-secondary'
            size='sm'
            onClick={() => {
              const collapsedState = {};
              questions.forEach((_, index) => {
                collapsedState[index] = true;
              });
              setCollapsedQuestions(collapsedState);
            }}
            disabled={questions.length === 0}
          >
            {t('form.collapse_all')}
          </Button>
          <Button
            type='button'
            variant='outline-secondary'
            size='sm'
            onClick={() => {
              const expandedState = {};
              questions.forEach((_, index) => {
                expandedState[index] = false;
              });
              setCollapsedQuestions(expandedState);
            }}
            disabled={questions.length === 0}
          >
            {t('form.expand_all')}
          </Button>
          <Button type='button' variant='primary' size='sm' onClick={addQuestion}>
            {t('form.add_question')}
          </Button>
        </div>
      </div>

      {questions.map((question, questionIndex) => {
        // Get the current question type from watched data
        const currentQuestionType = watchedQuestions?.[questionIndex]?.type || 'text';

        return (
          <Card key={question.id} className='mb-3'>
            <Card.Header className='d-flex justify-content-between align-items-center'>
              <div className='d-flex align-items-center flex-grow-1'>
                <Button
                  type='button'
                  variant='link'
                  size='sm'
                  className='p-0 me-2 text-decoration-none'
                  onClick={() => toggleQuestionCollapse(questionIndex)}
                >
                  {isQuestionCollapsed(questionIndex) ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                </Button>
                <div>
                  <div style={{ display: 'flex' }}>
                    <span>{t('form.question')} {questionIndex + 1}</span>
                    {isQuestionCollapsed(questionIndex) && watchedQuestions?.[questionIndex] && (
                      <>
                        {watchedQuestions[questionIndex].questionText && (
                          <div className='text-truncate fw-bold' style={{ maxWidth: '400px', color: '#333333', fontSize: '12px' }}>
                            &nbsp;:&nbsp;{watchedQuestions[questionIndex].questionText.length > 60
                              ? `${watchedQuestions[questionIndex].questionText.substring(0, 60)}...`
                              : watchedQuestions[questionIndex].questionText}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                  {isQuestionCollapsed(questionIndex) && watchedQuestions?.[questionIndex] && (
                    <div className='' style={{ color: '#ffffff' }}>
                      <div className='d-flex align-items-center gap-2'>
                        <span className='badge bg-secondary'>
                          {questionTypes.find(type => type.value === currentQuestionType)?.label || currentQuestionType}
                        </span>
                        {watchedQuestions[questionIndex].required && (
                          <span className='badge bg-danger' style={{ marginLeft: '5px' }}>Required</span>
                        )}
                        {watchedQuestions[questionIndex].questionType && (
                          <span className='badge bg-info' style={{ marginLeft: '5px' }}>
                            {questionTypeOptions.find(cat => cat.value === watchedQuestions[questionIndex].questionType)?.label || watchedQuestions[questionIndex].questionType}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <Button
                type='button'
                variant='danger'
                size='sm'
                onClick={() => removeQuestionItem(questionIndex)}
              >
                {t('form.remove')}
              </Button>
            </Card.Header>
            <Collapse in={!isQuestionCollapsed(questionIndex)}>
              <div>
                <Card.Body>
                  <Row>
                    <Col md={6}>
                      <Form.Group className='mb-2'>
                        <Form.Label>{t('form.question_type')} *</Form.Label>
                        <Form.Control
                          as='select'
                          {...register(`pages.${pageIndex}.questions.${questionIndex}.type`, { required: true })}
                        >
                          {questionTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className='mb-2'>
                        <Form.Label>{t('form.question_category')} *</Form.Label>
                        <Form.Control
                          as='select'
                          {...register(`pages.${pageIndex}.questions.${questionIndex}.questionType`, { required: true })}
                        >
                          {questionTypeOptions.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </Form.Control>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className='mb-2'>
                    <Form.Label>{t('form.question_text')} *</Form.Label>
                    <InputGroup>
                      <Form.Control
                        as='textarea'
                        rows={3}
                        placeholder={t('form.enter_question_text')}
                        {...register(`pages.${pageIndex}.questions.${questionIndex}.questionText`, { required: true })}
                      />
                      <Button
                        variant='outline-secondary'
                        onClick={() => openVariableModal && openVariableModal(`pages.${pageIndex}.questions.${questionIndex}.questionText`)}
                        title={t('form.insert_variable')}
                      >
                        {t('form.variables')}
                      </Button>
                    </InputGroup>
                    <FormInvalidFeedback
                      isInvalid={isFieldError(`pages.${pageIndex}.questions.${questionIndex}.questionText`, errors, errMessages)}
                    >
                      {getErrorMessage(`pages.${pageIndex}.questions.${questionIndex}.questionText`, errors, errMessages)}
                    </FormInvalidFeedback>
                  </Form.Group>

                  <Form.Group className='mb-2'>
                    <Form.Check
                      type='checkbox'
                      label={t('form.required')}
                      {...register(`pages.${pageIndex}.questions.${questionIndex}.required`)}
                    />
                  </Form.Group>

                  {/* Options section for rating, checkbox, radiogroup, dropdown */}
                  {requiresOptions(currentQuestionType) && (
                    <OptionsBuilder
                      pageIndex={pageIndex}
                      questionIndex={questionIndex}
                      control={control}
                      register={register}
                      setValue={setValue}
                      questionType={currentQuestionType}
                      t={t}
                    />
                  )}

                  {/* Visibility Logic */}
                  {watchedQuestions?.[questionIndex]?.logic ? (
                    <VisibilityLogic
                      pageIndex={pageIndex}
                      questionIndex={questionIndex}
                      control={control}
                      register={register}
                      setValue={setValue}
                      watch={watch}
                      t={t}
                    />
                  ) : (
                    // Show "Add Logic" button for questions that can have logic
                    questionIndex > 0 && watchedQuestions && watchedQuestions.length > 1 && (
                      <div className='mt-3'>
                        <Button
                          type='button'
                          variant='outline-secondary'
                          size='sm'
                          onClick={() => {
                            setValue(`pages.${pageIndex}.questions.${questionIndex}.logic`, {
                              trigger_question_index: null,
                              condition_type: 'equal',
                              condition_value: '',
                              action: 'show'
                            });
                          }}
                        >
                          {t('form.add_visibility_logic')}
                        </Button>
                      </div>
                    )
                  )}
                </Card.Body>
              </div>
            </Collapse>
          </Card>
        );
      })}

      {questions.length === 0 && (
        <div className='text-center text-muted py-4'>
          <p>{t('form.no_questions_added_yet')}</p>
          <Button type='button' variant='outline-primary' onClick={addQuestion}>
            {t('form.add_first_question')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default QuestionBuilder;